import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Surface } from '@cloudflare/kumo/components/surface';
import { Button } from '@cloudflare/kumo/components/button';
import { Banner } from '@cloudflare/kumo/components/banner';
import { Loader } from '@cloudflare/kumo/components/loader';
import { ArrowSquareOut, DownloadSimple, ArrowClockwise } from '@phosphor-icons/react';
import { api } from '@/lib/api';
import { formatJstDateLabel } from '@/lib/kiosk';

// 当日受付 (admin)。受付端末 (/kiosk) の入口と、受付数の確認、開催後の CSV 出力。
// 受付そのものは受付端末で行う。欠席・遅刻連絡は出展者詳細で日ごとに付ける。

interface DaySummaryRow {
  day: 1 | 2;
  done: number;
  total: number;
  lateCount: number;
  absentSeenCount: number;
  absentCount: number;
}

interface Summary {
  eventYear: number;
  eventDates: { day1: string; day2: string | null } | null;
  days: DaySummaryRow[];
  recordCount: number;
}

function dateLabel(iso: string | null | undefined): string {
  return iso ? formatJstDateLabel(iso) : '';
}

export default function CheckinsPage() {
  const [summary, setSummary] = useState<Summary | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    try {
      const res = await api.get<{ data: Summary }>('/api/admin/checkins/summary');
      setSummary(res.data.data);
    } catch {
      setError('受付状況の読み込みに失敗しました');
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const download = async () => {
    setDownloading(true);
    setError(null);
    try {
      const res = await api.get<Blob>('/api/admin/checkins/export', { responseType: 'blob' });
      const cd = res.headers['content-disposition'] as string | undefined;
      const filename = cd?.match(/filename="?([^"]+)"?/)?.[1] ?? 'nt-tokyo-checkins.csv';
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      setError('CSV のダウンロードに失敗しました');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-semibold text-kumo-default">当日受付</h1>
        <div className="flex gap-2">
          <Button variant="secondary" icon={ArrowClockwise} onClick={load}>
            再読み込み
          </Button>
          <Link to="/kiosk">
            <Button variant="primary" icon={ArrowSquareOut}>
              受付画面を開く
            </Button>
          </Link>
        </div>
      </div>

      {error && <Banner variant="error">{error}</Banner>}

      {!summary ? (
        !error && (
          <div className="flex justify-center py-12">
            <Loader />
          </div>
        )
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {summary.days.map((d) => (
            <Surface key={d.day} className="p-6">
              <div className="text-sm text-kumo-muted">
                {d.day} 日目 {dateLabel(d.day === 1 ? summary.eventDates?.day1 : summary.eventDates?.day2)}
              </div>
              <div className="mt-1 text-3xl font-semibold tabular-nums text-kumo-default">
                {d.done} <span className="text-lg text-kumo-muted">/ {d.total} 組 受付済み</span>
              </div>
              <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-sm text-kumo-default">
                <span>未受付 {d.total - d.done}</span>
                <span>遅刻連絡 {d.lateCount}</span>
                <span>巡回で不在 {d.absentSeenCount}</span>
                <span>欠席連絡 {d.absentCount}</span>
              </div>
              {d.day === 2 && (
                <p className="mt-3 text-xs text-kumo-muted">
                  2 日目は、日曜のみの出展者と、土曜に受付していない両日出展者だけを数えます (土曜に受付済みの両日出展者は受付不要)。
                </p>
              )}
            </Surface>
          ))}
        </div>
      )}

      <Surface className="p-6">
        <h2 className="text-base font-semibold text-kumo-default">受付記録の CSV</h2>
        <p className="mt-1 text-sm text-kumo-muted">
          受付・受付不要・要対応・巡回での不在確認をすべて時刻つきで出します (取り消した記録も取消日時つきで含みます)。
          到着時刻の分析には、経路が「本人の入力」の行を使ってください (「一覧から」はスタッフが操作した時刻です)。
        </p>
        <div className="mt-3">
          <Button variant="secondary" icon={DownloadSimple} loading={downloading} onClick={download}>
            CSV をダウンロード{summary ? ` (${summary.recordCount} 件)` : ''}
          </Button>
        </div>
      </Surface>

      <Surface className="p-6">
        <h2 className="text-base font-semibold text-kumo-default">受付端末の準備</h2>
        <ol className="mt-2 list-decimal space-y-1 pl-5 text-sm text-kumo-default">
          <li>受付端末用のメールアドレスで一度ログインし、「ユーザー」画面でそのアカウントを「受付端末にする」。</li>
          <li>iPad の Safari で管理画面を開いてそのアカウントでログインすると、受付画面になります。公開サイトのマイページでそのアカウントの Passkey を登録しておくと、再ログインのときにメールを待たずに済みます (受付端末のアカウントは出展者向けの機能を使えないので、マイページの申込状況などはエラー表示になりますが、パスキーの欄は使えます)。</li>
          <li>受付画面を開いた状態で、共有ボタン →「ホーム画面に追加」を押す。ホーム画面の「出展者受付」から開くと、Safari の枠が消えて全画面になる (アプリの中でもう一度ログインが必要)。</li>
          <li>
            iPad を横向きにしてから、そのアプリでアクセスガイドを開始する (トップボタンを 3 回押す)。おすすめの設定：タッチはオン、トップボタン・音量ボタン・動作・ソフトウェアキーボード・辞書で調べる・時間制限はオフ、画面の自動ロックは「しない」。電源につないでおく。
          </li>
          <li>受付画面のタイトル「NT東京2026 出展者受付」を 3 秒長押しすると管理者メニュー (受付の取消・未受付の一覧・端末の設定) が開きます。</li>
          <li>admin のアカウントでも受付画面を開けます (スマホで未受付の一覧を見ながら巡回するときなど)。</li>
        </ol>
        <p className="mt-2 text-xs text-kumo-muted">
          欠席・遅刻の連絡があった出展者は、出展者詳細の「当日の欠席・遅刻連絡」で日ごとに付けてください。
        </p>
      </Surface>
    </div>
  );
}
